import type { AIStructuredResult, AIMedication, AIExam, AILensRx } from '@/types/ai';
import type { EncounterExamsState } from '@/types/encounter';
import {
  serializeSurgeryRequest,
  hasSurgeryContent,
  type SurgeryProcedureItem,
} from '@/utils/surgeryRequest';
import { htmlToPlainText } from '@/utils/richText';
import { formatDateBR } from '@/utils/date';

/**
 * Session draft shared between PatientDetailPage (owns the state) and
 * PatientSessionPanel (consumes it via props). Lives in its own file so
 * the panel file can export only a component — required for Vite's
 * react-refresh HMR to work reliably.
 */

export interface ImageAttachment {
  id: string;
  file: File;
  previewUrl: string;
  /** "Usar como contexto da IA" — ausente = true (nasce marcada). Desmarcada
   *  no bloco "Contexto da IA", a imagem fica na sessão mas NÃO é enviada
   *  na extração. */
  includeInAI?: boolean;
}

export interface PdfAttachment {
  id: string;
  file: File;
  /** Mesmo contrato do `ImageAttachment.includeInAI` (ausente = true). */
  includeInAI?: boolean;
}

/**
 * Flags de inclusão das FONTES DE TEXTO do atendimento no contexto da IA —
 * controladas pelo bloco "Contexto da IA" antes de extrair. Ausente/true =
 * entra (padrão marcado, caso comum continua 1 clique); false = fica fora
 * da extração, sem apagar nada.
 */
export interface AIContextIncludeFlags {
  /** Observações do médico (campo de texto da sessão). */
  sessionText?: boolean;
  /** Card "Texto Simples". */
  textoSimples?: boolean;
  /** Evolução digitada no editor do prontuário (draft do AIDataStore). */
  prontuarioText?: boolean;
  /** Painel "Exames do atendimento" (PIO, refração…). */
  encounterExams?: boolean;
}

/** Lê uma flag de contexto: ausente = true (padrão marcado). */
export function aiContextOn(
  flags: AIContextIncludeFlags | undefined,
  key: keyof AIContextIncludeFlags,
): boolean {
  return flags?.[key] !== false;
}

/**
 * Action kinds for the editable panels below the inputs. `prontuario` is
 * the full clinical record (evolução); `conduta` is a follow-up note; the
 * others map 1-1 to Prescription types when saved to Firestore.
 */
export type ActionKind =
  | 'prontuario'
  | 'exames'
  | 'receita'
  | 'oculos'
  | 'atestado'
  | 'laudo'
  | 'conduta'
  | 'cirurgias'
  | 'declaracao'
  // laudoSus não tem draft próprio no SessionDraft — o estado do modal
  // (template escolhido + valores dos campos) é efêmero, vive no
  // PatientSessionPanel. Entra no ActionKind só pra controlar o badge de
  // "salvo/imprimindo" e reusar o mesmo switch de modais abertos.
  | 'laudoSus'
  // Encaminhamento — saiu do "Pedido de Exames" e virou ação própria.
  // Salva como Prescription type=REFERRAL (mesmo tipo de cirurgias).
  | 'encaminhamento'
  // Solicitação combinada (exames e/ou cirurgia) do modal "Solicitações".
  // Usado só pros badges saving/saved/printing — os drafts continuam em
  // exames/examesNotes/cirurgias (principal) e SolicitacaoInstance (extras).
  | 'solicitacao';

/**
 * Conteúdo efetivo de UMA instância de solicitação (principal ou extra),
 * resolvido pelo modal e passado aos handlers de salvar/imprimir. `isMain`
 * marca a principal (única que a IA preenche → alimenta `aiGenerated`).
 */
export interface SolicitacaoPayload {
  exams: AIExam[];
  notes: string;
  /** Texto efetivo do pedido cirúrgico (procedimentos serializados + livre). */
  surgText: string;
  /** Procedimentos estruturados — gravados em `content.procedures`. */
  surgProcedures: SurgeryProcedureItem[];
  /** Só o texto livre — gravado em `content.notes`. */
  surgNotes: string;
  includeExams: boolean;
  includeSurg: boolean;
  isMain: boolean;
  /** Id da instância extra de origem (`undefined` na principal) — permite
   *  marcar a instância certa como "já salva" (a seleção de instância do
   *  modal Solicitações é local, não usa o editingInstanceId global). */
  instanceId?: string;
}

/**
 * Subset of ActionKind that can also serve as a *manual input* — the
 * doctor fills one of these draft editors directly (instead of or in
 * addition to the audio/text/pdf/image inputs) and can either save it
 * as an independent prescription record or flag it to be sent as
 * context to the AI extraction call.
 */
export type InputDraftKind = 'exames' | 'receita' | 'oculos' | 'atestado' | 'cirurgias' | 'declaracao' | 'encaminhamento' | 'laudo' | 'conduta';

/**
 * Per-draft flag telling the extraction pipeline whether this manual
 * content should be serialised and injected into the Gemini prompt as
 * "already prescribed/already written by the doctor" context.
 */
export type IncludeInAIFlags = Record<InputDraftKind, boolean>;

export const EMPTY_INCLUDE_IN_AI: IncludeInAIFlags = {
  exames: true,
  receita: true,
  oculos: true,
  atestado: true,
  cirurgias: true,
  declaracao: true,
  encaminhamento: true,
  laudo: true,
  conduta: true,
};

/**
 * Per-action editable drafts. Populated by "Extrair Dados" from the AI
 * structured result, then freely modifiable by the user inside each
 * action modal until they click "Salvar" (persists to the patient's
 * record) or click "Extrair Dados" again (overwrites all drafts).
 */
export interface ActionDrafts {
  prontuario: string;            // full evolução markdown
  exames: AIExam[];              // list of exam requests
  examesNotes: string;           // free-text notes/orientações for exam request
  receita: AIMedication[];       // list of medications
  receitaNotes: string;          // free-text notes/orientações for prescription
  oculos: AILensRx | null;       // lens prescription
  atestado: string;              // attestation text
  laudo: string;                 // medical report text
  conduta: string;               // conduct suggestion text
  cirurgias: string;             // surgery/procedure — TEXTO LIVRE do médico
  /** Procedimentos estruturados do pedido cirúrgico (olho/prazo por item). */
  cirurgiasProcs: SurgeryProcedureItem[];
  declaracao: string;            // declaration/report text
  encaminhamento: string;        // referral text (especialidade/serviço + motivo)
}

export const EMPTY_DRAFTS: ActionDrafts = {
  prontuario: '',
  exames: [],
  examesNotes: '',
  receita: [],
  receitaNotes: '',
  oculos: null,
  atestado: '',
  laudo: '',
  conduta: '',
  cirurgias: '',
  cirurgiasProcs: [],
  declaracao: '',
  encaminhamento: '',
};

/**
 * Tipos de documentos que podem ter múltiplas instâncias na mesma sessão
 * (ex: 2 atestados, 2 receitas de óculos). Cada `kind` aqui mapeia 1-1 a
 * um campo do `ActionDrafts` que continua sendo a "instância principal" —
 * as instâncias adicionais vivem em `SessionDraft.extraInstances`.
 *
 * `solicitacao` é a instância COMBINADA do modal "Solicitações" (exames
 * e/ou cirurgia num documento só — 1 instância = 1 PDF). Os kinds legados
 * `exames`/`cirurgias` continuam aceitos na união pra migração de rascunhos
 * persistidos (ver `migrateExtraInstances`), mas a UI não cria mais deles.
 */
export type InstanceKind = 'atestado' | 'cirurgias' | 'declaracao' | 'oculos' | 'exames' | 'receita' | 'solicitacao';

interface BaseInstance {
  id: string;
  /** Rótulo opcional digitado pelo médico pra distinguir (ex: "Para o INSS"). */
  label?: string;
}

export interface TextDocumentInstance extends BaseInstance {
  kind: 'atestado' | 'cirurgias' | 'declaracao';
  text: string;
}

export interface LensDocumentInstance extends BaseInstance {
  kind: 'oculos';
  rx: AILensRx;
}

export interface ExamDocumentInstance extends BaseInstance {
  kind: 'exames';
  exams: AIExam[];
  notes: string;
}

export interface MedicationDocumentInstance extends BaseInstance {
  kind: 'receita';
  meds: AIMedication[];
  notes: string;
}

/**
 * Instância do modal "Solicitações" — pode conter exames E/OU cirurgia.
 * É a unidade de impressão: o que estiver junto na mesma instância sai
 * junto num único PDF; instâncias diferentes imprimem separadas. No
 * Firestore o salvamento cria UM EXAM_REQUEST (lista completa de exames)
 * e/ou UM pedido cirúrgico — só o impresso combina as duas partes.
 */
export interface SolicitacaoInstance extends BaseInstance {
  kind: 'solicitacao';
  exams: AIExam[];
  notes: string;
  /** Texto livre da solicitação de cirurgia/procedimento (HTML do editor). */
  surgText: string;
  /** Procedimentos estruturados. Ausente em instâncias criadas antes da
   *  estrutura — leia sempre com `?? []`. */
  surgProcs?: SurgeryProcedureItem[];
  /** Seções habilitadas nesta instância ("O que entra nesta solicitação"). */
  includeExams: boolean;
  includeSurg: boolean;
  /** "Usar como contexto para a IA" — POR INSTÂNCIA (diferente dos demais
   *  kinds, cujo flag é por tipo em `includeInAI`). Default true. */
  useInAI: boolean;
}

export type DocumentInstance =
  | TextDocumentInstance
  | LensDocumentInstance
  | ExamDocumentInstance
  | MedicationDocumentInstance
  | SolicitacaoInstance;

/**
 * Migra instâncias extras persistidas no formato antigo (kinds separados
 * `exames`/`cirurgias`) pro formato combinado `solicitacao`. Idempotente —
 * instâncias já migradas e os demais kinds passam intactos. Roda na
 * restauração do rascunho (sessionDraftStore) e é seguro rodar de novo.
 */
export function migrateExtraInstances(list: DocumentInstance[]): DocumentInstance[] {
  return list.map((inst): DocumentInstance => {
    if (inst.kind === 'exames') {
      return {
        id: inst.id,
        label: inst.label,
        kind: 'solicitacao',
        exams: inst.exams,
        notes: inst.notes,
        surgText: '',
        includeExams: true,
        includeSurg: false,
        useInAI: true,
      };
    }
    if (inst.kind === 'cirurgias') {
      return {
        id: inst.id,
        label: inst.label,
        kind: 'solicitacao',
        exams: [],
        notes: '',
        surgText: inst.text,
        includeExams: false,
        includeSurg: true,
        useInAI: true,
      };
    }
    return inst;
  });
}

/**
 * Kinds de documento que geram registro na ficha ao salvar e participam do
 * "salvo → registro → editor reabre em branco". Os cinco primeiros são os
 * textuais do modal Documento de Texto (snapshot = o próprio texto);
 * `receita`/`oculos` são os drafts principais estruturados (snapshot =
 * serialização JSON via `receitaSnapshot`/`oculosSnapshot`);
 * `solicitacaoExames`/`solicitacaoCirurgia` são as DUAS partes da
 * solicitação principal — commitam separadas pra nunca limpar uma parte
 * que não entrou no documento salvo; `solicitacao` só existe pra
 * instâncias extras do modal Solicitações (nunca aparece em `main`).
 */
export type CommittedDocKind =
  | 'prontuario'
  | 'atestado'
  | 'declaracao'
  | 'encaminhamento'
  | 'conduta'
  | 'receita'
  | 'oculos'
  | 'solicitacaoExames'
  | 'solicitacaoCirurgia'
  | 'solicitacao';

/**
 * Snapshots do conteúdo JÁ SALVO na ficha por documento. Gravados no
 * "Salvar" (inclusive Salvar e Imprimir — imprimir sempre salva antes) e
 * consumidos por `pruneCommittedDocs` ao abrir/fechar cada modal de
 * documento: conteúdo idêntico ao snapshot já virou registro (fica só no
 * histórico da ficha) e sai do rascunho — o editor reabre em branco pra
 * próxima entrada. Conteúdo editado DEPOIS do salvar difere do snapshot e
 * é preservado (é alteração ainda não salva).
 */
export interface CommittedDocs {
  /** Draft principal por kind → snapshot no momento do último salvar
   *  (texto cru nos kinds textuais; JSON nos estruturados). */
  main?: Partial<Record<CommittedDocKind, string>>;
  /** Instâncias extras (qualquer kind com instância): id → snapshot. */
  instances?: Record<string, string>;
}

/** Snapshot de receita (principal ou instância): medicamentos + orientações. */
export function receitaSnapshot(meds: AIMedication[], notes: string): string {
  return JSON.stringify({ meds, notes });
}

/** Snapshot de receita de óculos (principal ou instância). */
export function oculosSnapshot(rx: AILensRx): string {
  return JSON.stringify(rx);
}

/** Snapshot da PARTE de exames da solicitação principal. */
export function solicitacaoExamesSnapshot(exams: AIExam[], notes: string): string {
  return JSON.stringify({ exams, notes });
}

/** Snapshot de UMA instância extra de solicitação (unidade de impressão).
 *  `label`/`useInAI` ficam de fora de propósito: são metadados — mudá-los
 *  depois do salvar não torna o conteúdo "não salvo". */
export function solicitacaoSnapshot(
  p: Pick<
    SolicitacaoInstance,
    'exams' | 'notes' | 'surgText' | 'surgProcs' | 'includeExams' | 'includeSurg'
  >,
): string {
  return JSON.stringify({
    exams: p.exams,
    notes: p.notes,
    surgText: p.surgText,
    surgProcs: p.surgProcs ?? [],
    includeExams: p.includeExams,
    includeSurg: p.includeSurg,
  });
}

/** Kinds textuais cujo snapshot é o próprio texto do draft principal. */
const COMMITTED_TEXT_KINDS = [
  'prontuario',
  'atestado',
  'declaracao',
  'encaminhamento',
  'conduta',
] as const;

/**
 * Tira do rascunho os documentos cujo conteúdo já foi salvo na ficha
 * (idêntico ao snapshot em `committedDocs`): o que já é registro fica só
 * no histórico e o modal correspondente abre em branco pra próxima
 * entrada. Conteúdo que mudou depois do salvar é mantido (edição ainda não
 * salva). Os snapshots são sempre descartados ao final — um novo salvar
 * grava snapshots novos. Chamada ao abrir e ao fechar os modais de
 * documento (Documento de Texto, Receita, Óculos, Solicitações).
 */
export function pruneCommittedDocs(s: SessionDraft): SessionDraft {
  const committed = s.committedDocs;
  if (!committed) return s;
  const drafts = { ...s.drafts };
  const main = committed.main ?? {};

  for (const kind of COMMITTED_TEXT_KINDS) {
    const snap = main[kind];
    if (snap !== undefined && drafts[kind] === snap) drafts[kind] = '';
  }

  if (
    main.receita !== undefined &&
    receitaSnapshot(drafts.receita, drafts.receitaNotes) === main.receita
  ) {
    drafts.receita = [];
    drafts.receitaNotes = '';
  }

  if (
    main.oculos !== undefined &&
    drafts.oculos !== null &&
    oculosSnapshot(drafts.oculos) === main.oculos
  ) {
    drafts.oculos = null;
  }

  // Partes da solicitação principal — cada uma só some se foi ELA que
  // entrou no documento salvo e não mudou desde então.
  let solicPruned = false;
  if (
    main.solicitacaoExames !== undefined &&
    solicitacaoExamesSnapshot(drafts.exames, drafts.examesNotes) === main.solicitacaoExames
  ) {
    drafts.exames = [];
    drafts.examesNotes = '';
    solicPruned = true;
  }
  if (
    main.solicitacaoCirurgia !== undefined &&
    serializeSurgeryRequest(drafts.cirurgiasProcs ?? [], drafts.cirurgias) ===
      main.solicitacaoCirurgia
  ) {
    drafts.cirurgias = '';
    drafts.cirurgiasProcs = [];
    solicPruned = true;
  }
  // Solicitação principal totalmente esvaziada pelo prune → volta os
  // toggles "O que entra" ao default (exames ligado, cirurgia derivada do
  // conteúdo), senão a próxima solicitação abriria com seção vazia ligada.
  const solicitacaoMain =
    solicPruned &&
    drafts.exames.length === 0 &&
    !drafts.examesNotes.trim() &&
    !hasSurgeryContent(drafts.cirurgiasProcs ?? [], drafts.cirurgias)
      ? undefined
      : s.solicitacaoMain;

  const instSnapshots = committed.instances ?? {};
  const extraInstances = s.extraInstances.filter((inst) => {
    const snap = instSnapshots[inst.id];
    if (snap === undefined) return true;
    switch (inst.kind) {
      case 'atestado':
      case 'declaracao':
        return inst.text !== snap;
      case 'receita':
        return receitaSnapshot(inst.meds, inst.notes) !== snap;
      case 'oculos':
        return oculosSnapshot(inst.rx) !== snap;
      case 'solicitacao':
        return solicitacaoSnapshot(inst) !== snap;
      default:
        // Kinds legados ('exames'/'cirurgias' pré-migração) nunca recebem
        // snapshot — se aparecer um id coincidente, não remove.
        return true;
    }
  });

  return { ...s, drafts, extraInstances, solicitacaoMain, committedDocs: undefined };
}

/**
 * Registro anterior do prontuário (item da timeline) que o médico marcou
 * pra entrar como CONTEXTO na extração da IA. Guardamos um snapshot leve
 * (não só o id) pra qualquer caminho de extração — aba Prontuário, painel
 * ou sidebar — conseguir montar o contexto sem recarregar a timeline.
 */
export interface SelectedHistoryRecord {
  /** ID composto `${kind}-${id}` do item da timeline. */
  id: string;
  /** Rótulo do tipo (ex.: "Evolução Clínica", "Encaminhamento"). */
  label: string;
  /** epoch ms do registro (createdAt) — usado pra datar o bloco no prompt. */
  dateMs: number;
  /** Texto puro do registro, capturado no momento da marcação. */
  text: string;
}

/**
 * Session draft: kept alive in PatientDetailPage state so tab switches
 * inside the patient file don't discard audio/text/PDFs/images or the
 * generated text. Only cleared when:
 *   1. The user explicitly removes items (x / "Limpar sessão"), or
 *   2. The user leaves the patient file (PatientDetailPage unmounts /
 *      :id param changes) — revocation of preview URLs is handled by
 *      PatientDetailPage in those cases.
 */
export interface SessionDraft {
  audioBlobs: Blob[];
  /**
   * Flag por áudio (paralela a `audioBlobs`, mesmo índice): se aquele áudio
   * entra como contexto da IA nas extrações/análises do dia. Índice ausente
   * = true (todo áudio novo nasce marcado). Controlada pelas caixas de
   * seleção nas telas de gravação (aba Prontuário e modal da gaveta).
   */
  audioIncludeInAI?: boolean[];
  sessionText: string;
  pdfs: PdfAttachment[];
  images: ImageAttachment[];
  /** Full markdown returned by the last "Extrair Dados" run (editable). */
  extractedMarkdown: string;
  /** True once "Extrair Dados" has been run at least once this session. */
  hasExtracted: boolean;
  /** Per-action editable drafts, initialised from the AI result. */
  drafts: ActionDrafts;
  /** True while the AI extraction request is in flight. */
  busyExtracting: boolean;
  /**
   * Flags set by the "Usar como contexto para a IA" checkbox inside
   * each input draft modal. When true, the serialised content of the
   * matching draft is appended to the Gemini extraction prompt.
   */
  includeInAI: IncludeInAIFlags;
  /**
   * Instâncias adicionais por tipo de documento — permitem múltiplos
   * atestados, receitas de óculos etc. no mesmo atendimento. A primeira
   * instância de cada tipo continua sendo `drafts.<tipo>` (a "principal");
   * as extras vivem aqui. A IA continua extraindo só pra principal —
   * extras são preenchidas manualmente pelo médico.
   */
  extraInstances: DocumentInstance[];
  /**
   * Seções habilitadas na instância PRINCIPAL do modal "Solicitações"
   * (exames = drafts.exames/examesNotes; cirurgia = drafts.cirurgias).
   * Ausente = exames ligado, cirurgia ligada só se drafts.cirurgias tem
   * conteúdo (comportamento herdado dos dois cards antigos).
   */
  solicitacaoMain?: { includeExams: boolean; includeSurg: boolean };
  /**
   * Instruções livres do médico pra orientar a extração da IA. Texto puro
   * que é anexado ao prompt antes do contexto. Ex: "Redigir laudo de DMRI
   * exsudativa OD", "Preencher solicitação de cirurgia de catarata OE com
   * LIO multifocal". Vazio = nenhuma instrução adicional.
   */
  aiInstructions: string;
  /**
   * Acompanhante do dia — paciente cadastrado que veio junto com o
   * paciente nesta consulta. Varia consulta a consulta (não persiste no
   * cadastro permanente do paciente). Usado pra:
   *   1. Mostrar quem está acompanhando esta sessão
   *   2. Pré-preencher `{{acompanhante}}` na declaração de acompanhante
   *      ao imprimir
   * Obrigatoriamente um paciente cadastrado — não aceita texto livre.
   */
  accompanistId?: string;
  /** Nome cached pra exibição imediata na UI sem refazer o getDoc. */
  accompanistName?: string;
  /** CPF descriptografado do acompanhante, cached na seleção pra alimentar
   *  `{{acompanhante_cpf}}` na impressão da declaração sem refazer o
   *  decrypt a cada print. */
  accompanistCpf?: string;
  /**
   * Exames modulares do atendimento (Lensometria, Refração, PIO, Biometria…)
   * agrupados por "Tipo de atendimento" (preset). Definido em
   * `types/encounter.ts`. Segue a MESMA regra de persistência do resto da
   * sessão: vive em memória durante a edição e é gravado no IndexedDB
   * (sessionDraftStore) pra sobreviver à troca de página/aba, até o médico
   * salvar no prontuário, limpar a sessão ou sair (logout / virada do dia).
   */
  encounterExams?: EncounterExamsState;
  /**
   * Conteúdo do card "Texto Simples" — texto/HTML livre que o médico
   * digita pra imprimir sem cabeçalho/rodapé/carimbo (a moldura é
   * controlada por `simpleText*` em `settings/print`). Não é salvo na
   * ficha do paciente; vive só na sessão.
   */
  textoSimples?: string;
  /**
   * Carry-over dos exames modulares: marca que já tentamos pré-popular o
   * painel a partir da última consulta (evita reexecução em remontagem de
   * aba ou após "Limpar"), e a data da consulta de origem (pro banner
   * "valores da última consulta"). Persistem junto com `encounterExams` no
   * sessionDraftStore — assim, ao voltar pra ficha, não refazemos o carry-over
   * (leitura do Firestore) nem perdemos o banner.
   */
  encounterCarryOverDone?: boolean;
  encounterCarryOverFromDate?: number;
  /**
   * Marca que já tentamos semear a "ficha base" (módulos do tipo de
   * atendimento padrão configurado em Configurações) neste atendimento.
   * Roda uma única vez, DEPOIS do carry-over e só quando o painel está
   * vazio — assim retorno usa os módulos da última consulta, paciente
   * novo abre com a ficha base, e "Limpar" não re-semeia.
   */
  encounterFichaBaseSeeded?: boolean;
  /**
   * Sugestão de preenchimento de um laudo PDF vinda da extração por IA
   * (quando as Instruções para IA pedem "preencha o laudo X"). `values`
   * mapeia {nomeInternoDoCampo: valor} dos campos clínicos. Consumida pelo
   * LaudoSusModal (que abre automaticamente) e limpa ao fechar. Não passa
   * pelo merge de drafts — laudoSus não tem draft próprio.
   */
  laudoSusSuggestion?: { templateId?: string; values: Record<string, string> };
  /**
   * Registros ANTERIORES do prontuário (timeline) que o médico marcou pra
   * entrar como contexto na próxima extração da IA. Selecionados pelo botão
   * de inclusão em cada registro (lista e painel de detalhe da aba
   * Prontuário). Persistem com o resto da sessão (sessionDraftStore) até
   * salvar/limpar/sair. Vazio/ausente = nenhum registro antigo no contexto.
   */
  selectedHistory?: SelectedHistoryRecord[];
  /**
   * Flags das fontes de TEXTO do atendimento no contexto da IA (observações,
   * texto simples, evolução digitada, exames do atendimento). Ausente = tudo
   * entra (padrão). Ver `AIContextIncludeFlags`/`aiContextOn`.
   */
  aiContextInclude?: AIContextIncludeFlags;
  /**
   * Documentos já salvos na ficha nesta sessão (ver `CommittedDocs`).
   * Persistem com o resto do rascunho pra o prune funcionar mesmo após
   * reload/troca de aba.
   */
  committedDocs?: CommittedDocs;
  /**
   * Documentos que JÁ FORAM emitidos (salvos na ficha) nesta sessão, por
   * kind. Diferente de `committedDocs`, isto NÃO é consumido pelo prune —
   * fica marcado até o atendimento terminar. Serve pra o editor não
   * "ressuscitar" um documento já emitido ao reabrir: a Receita de Óculos,
   * por exemplo, para de ser pré-preenchida com a refração do atendimento
   * depois que a receita daquela refração já saiu impressa/salva.
   */
  issuedDocs?: Partial<Record<CommittedDocKind, true>>;
}

export const EMPTY_SESSION: SessionDraft = {
  audioBlobs: [],
  audioIncludeInAI: [],
  sessionText: '',
  pdfs: [],
  images: [],
  extractedMarkdown: '',
  hasExtracted: false,
  drafts: EMPTY_DRAFTS,
  busyExtracting: false,
  includeInAI: EMPTY_INCLUDE_IN_AI,
  extraInstances: [],
  aiInstructions: '',
  selectedHistory: [],
};

/**
 * Flags de "usar na IA" normalizadas pro tamanho de `blobs`: índice sem
 * flag persistida vale `true` (padrão marcado). Usar antes de alterar a
 * lista (remover/alternar) pra manter os índices alinhados aos blobs.
 */
export function normalizedAudioFlags(blobs: Blob[], flags?: boolean[]): boolean[] {
  return blobs.map((_, i) => flags?.[i] !== false);
}

/** Só os áudios marcados pra entrar no contexto da IA. */
export function filterAudioForAI(blobs: Blob[], flags?: boolean[]): Blob[] {
  return blobs.filter((_, i) => flags?.[i] !== false);
}

/** Só as imagens marcadas pra entrar no contexto da IA (ausente = marcada). */
export function filterImagesForAI(images: ImageAttachment[]): ImageAttachment[] {
  return images.filter((i) => i.includeInAI !== false);
}

/** Só os PDFs marcados pra entrar no contexto da IA (ausente = marcado). */
export function filterPdfsForAI(pdfs: PdfAttachment[]): PdfAttachment[] {
  return pdfs.filter((p) => p.includeInAI !== false);
}

/**
 * Revoke every object URL inside a session draft.
 * Called by PatientDetailPage on patient-file unmount and on patient switch.
 */
export function revokeSessionUrls(session: SessionDraft): void {
  session.images.forEach((img) => URL.revokeObjectURL(img.previewUrl));
}

/**
 * Build a markdown representation of the AI-extracted evolução so the
 * "Prontuário" action button can show it as editable text.
 */
export function evolucaoToMarkdown(result: AIStructuredResult | null): string {
  if (!result?.evolucao) return '';
  const ev = result.evolucao;
  const hd =
    ev.hipotesesDiagnosticas && ev.hipotesesDiagnosticas.length > 0
      ? ev.hipotesesDiagnosticas.map((h) => `- ${h}`).join('\n')
      : '—';
  return [
    '## Queixa Principal',
    ev.queixaPrincipal || '—',
    '',
    '## História da Moléstia Atual',
    ev.historiaAtual || '—',
    ...(ev.antecedentesPessoais?.trim()
      ? ['', '## Antecedentes Pessoais', ev.antecedentesPessoais.trim()]
      : []),
    ...(ev.antecedentesFamiliares?.trim()
      ? ['', '## Antecedentes Familiares', ev.antecedentesFamiliares.trim()]
      : []),
    '',
    '## Exame Físico',
    ev.exameFisico || '—',
    '',
    '## Hipóteses Diagnósticas',
    hd,
    ev.cid ? `\nCID-10: ${ev.cid}` : '',
    '',
    '## Conduta',
    ev.conduta || '—',
  ]
    .join('\n')
    .trim();
}

/**
 * Map a full AI structured result into the per-action drafts format.
 * Used by "Extrair Dados" to populate all editable fields in one call.
 */
export function structuredResultToDrafts(
  result: AIStructuredResult | null,
  fallbackMarkdown: string,
): ActionDrafts {
  if (!result) {
    return {
      ...EMPTY_DRAFTS,
      prontuario: fallbackMarkdown,
    };
  }
  const docs = result.documentos ?? [];
  const findDoc = (tipo: string): string =>
    docs.find((d) => d.tipo === tipo)?.conteudo ?? '';
  return {
    prontuario: evolucaoToMarkdown(result) || fallbackMarkdown,
    exames: result.exames ?? [],
    examesNotes: '',
    receita: result.medicamentos ?? [],
    receitaNotes: '',
    oculos: result.receitaOculos ?? null,
    atestado: findDoc('ATESTADO') || findDoc('CERTIFICADO'),
    // O laudo gerado pela IA (documentos[] tipo LAUDO) vai pro editor
    // "Laudos / Declarações" (`declaracao`) — o que tem modelos, tokens de
    // exame e impressão. O "Laudo Médico" (`laudo`) deixou de ser alvo
    // automático da IA (continua acessível pra preenchimento manual).
    // Encaminhamento tem tipo/draft próprio.
    laudo: '',
    conduta: result.evolucao?.conduta ?? '',
    cirurgias: '',
    cirurgiasProcs: [],
    declaracao: findDoc('LAUDO'),
    encaminhamento: findDoc('ENCAMINHAMENTO'),
  };
}

/**
 * Merge an AI extraction result into the current editable drafts
 * WITHOUT overwriting manually entered content. Rules:
 *
 *  - **Strings** (prontuario, atestado, laudo, conduta): if the
 *    existing value is non-empty, keep it; otherwise take the AI
 *    version.
 *  - **Lists** (exames, receita): concatenate — existing items come
 *    first, then AI items whose `nome` does not already appear in the
 *    existing list (case-insensitive). Never removes items.
 *  - **Object** (oculos): if the doctor already filled a lens
 *    prescription, keep it; otherwise take the AI version.
 *
 * This is the "IA complementa/melhora" behaviour chosen by the user:
 * the doctor's manual drafts are authoritative and the AI only fills
 * gaps or adds new items.
 */
export function mergeDraftsWithAI(
  existing: ActionDrafts,
  result: AIStructuredResult | null,
  fallbackMarkdown: string,
): ActionDrafts {
  const ai = structuredResultToDrafts(result, fallbackMarkdown);

  const normalizeName = (s: string): string => s.trim().toLowerCase();

  const existingExamNames = new Set(existing.exames.map((e) => normalizeName(e.nome)));
  const newExamsFromAI = ai.exames.filter(
    (e) => e.nome.trim() && !existingExamNames.has(normalizeName(e.nome)),
  );

  const existingMedNames = new Set(existing.receita.map((m) => normalizeName(m.nome)));
  const newMedsFromAI = ai.receita.filter(
    (m) => m.nome.trim() && !existingMedNames.has(normalizeName(m.nome)),
  );

  return {
    prontuario: existing.prontuario.trim() ? existing.prontuario : ai.prontuario,
    exames: [...existing.exames, ...newExamsFromAI],
    examesNotes: existing.examesNotes,
    receitaNotes: existing.receitaNotes,
    receita: [...existing.receita, ...newMedsFromAI],
    oculos: existing.oculos ?? ai.oculos,
    atestado: existing.atestado.trim() ? existing.atestado : ai.atestado,
    laudo: existing.laudo.trim() ? existing.laudo : ai.laudo,
    conduta: existing.conduta.trim() ? existing.conduta : ai.conduta,
    cirurgias: existing.cirurgias.trim() ? existing.cirurgias : ai.cirurgias,
    // A IA não preenche procedimentos estruturados — o que o médico montou
    // no editor passa intacto.
    cirurgiasProcs: existing.cirurgiasProcs ?? [],
    declaracao: existing.declaracao.trim() ? existing.declaracao : ai.declaracao,
    encaminhamento: existing.encaminhamento.trim() ? existing.encaminhamento : ai.encaminhamento,
  };
}

/**
 * Serialise the drafts whose "Usar como contexto para a IA" flag is
 * on into a markdown block that can be appended to the extraction
 * prompt. The AI is told these items are "already prescribed/written"
 * by the doctor so it can complement them rather than restart from
 * scratch.
 */
export function serializeDraftsForAI(
  drafts: ActionDrafts,
  flags: IncludeInAIFlags,
  extraInstances: DocumentInstance[] = [],
): string {
  const sections: string[] = [];

  // Os campos de texto livre podem chegar aqui como HTML (quando o
  // médico usou o RichTextEditor) ou como string pura (formato antigo).
  // Sempre normalizamos para texto puro antes de injetar no prompt — IA
  // não precisa ver `<b>`, `<span style>` etc, e isso mantém o consumo
  // de tokens igual ao de antes da migração.
  const toPrompt = (raw: string): string => htmlToPlainText(raw).trim();

  const fmtLens = (e: { esferica: string; cilindrica: string; eixo: string; adicao: string; dp: string; av: string }): string =>
    `esf ${e.esferica || '—'}, cil ${e.cilindrica || '—'}, eixo ${e.eixo || '—'}, add ${
      e.adicao || '—'
    }, DNP ${e.dp || '—'}, AV ${e.av || '—'}`;

  const fmtExams = (exams: AIExam[]): string[] =>
    exams
      .filter((e) => e.nome.trim())
      .map((e) => {
        const parts = [`- ${e.nome}`];
        if (e.justificativa.trim()) parts.push(`(${e.justificativa})`);
        if (e.cid.trim()) parts.push(`[CID ${e.cid}]`);
        return parts.join(' ');
      });

  const fmtMeds = (meds: AIMedication[]): string[] =>
    meds
      .filter((m) => m.nome.trim())
      .map((m) => {
        const parts = [`- ${m.nome}`];
        if (m.dosagem.trim()) parts.push(m.dosagem);
        const detailParts: string[] = [];
        if (m.posologia.trim()) detailParts.push(m.posologia);
        if (m.via.trim()) detailParts.push(`via ${m.via}`);
        if (m.duracao.trim()) detailParts.push(`${m.duracao}`);
        if (m.quantidade.trim()) detailParts.push(`qtd ${m.quantidade}`);
        if (detailParts.length > 0) parts.push('—', detailParts.join(', '));
        return parts.join(' ');
      });

  // ── Exames (principal + extras) ──
  // Extras: instâncias legadas kind 'exames' seguem a flag do tipo; as
  // combinadas 'solicitacao' têm flag PRÓPRIA (inst.useInAI) e só entram
  // quando a seção de exames está habilitada nelas.
  {
    const allItems: string[] = flags.exames ? fmtExams(drafts.exames) : [];
    const notes: string[] = [];
    if (flags.exames) {
      const mainNotes = toPrompt(drafts.examesNotes);
      if (mainNotes) notes.push(mainNotes);
    }
    for (const inst of extraInstances) {
      const isLegacy = inst.kind === 'exames' && flags.exames;
      const isSolic = inst.kind === 'solicitacao' && inst.useInAI && inst.includeExams;
      if (!isLegacy && !isSolic) continue;
      const label = inst.label ? ` [${inst.label}]` : '';
      const extra = fmtExams((inst as ExamDocumentInstance | SolicitacaoInstance).exams);
      if (extra.length > 0) allItems.push(...extra.map((l) => l + label));
      const n = toPrompt((inst as ExamDocumentInstance | SolicitacaoInstance).notes);
      if (n) notes.push(inst.label ? `[${inst.label}] ${n}` : n);
    }
    if (allItems.length > 0 || notes.length > 0) {
      let block = '### Exames já solicitados manualmente pelo médico (NÃO remova, pode complementar com itens novos):';
      if (allItems.length > 0) block += '\n' + allItems.join('\n');
      if (notes.length > 0) block += '\nOrientações/observações: ' + notes.join(' | ');
      sections.push(block);
    }
  }

  // ── Receita (principal + extras) ──
  if (flags.receita) {
    const allItems: string[] = fmtMeds(drafts.receita);
    for (const inst of extraInstances) {
      if (inst.kind !== 'receita') continue;
      const label = inst.label ? ` [${inst.label}]` : '';
      const extra = fmtMeds(inst.meds);
      if (extra.length > 0) allItems.push(...extra.map((l) => l + label));
    }
    // Orientações/posologia em texto livre da receita (principal + extras) —
    // antes eram descartadas; agora entram como contexto clínico.
    const notes: string[] = [];
    const mainNotes = toPrompt(drafts.receitaNotes);
    if (mainNotes) notes.push(mainNotes);
    for (const inst of extraInstances) {
      if (inst.kind !== 'receita') continue;
      const n = toPrompt(inst.notes);
      if (n) notes.push(inst.label ? `[${inst.label}] ${n}` : n);
    }
    if (allItems.length > 0 || notes.length > 0) {
      let block = '### Medicamentos já prescritos manualmente pelo médico (NÃO remova, pode complementar com itens novos):';
      if (allItems.length > 0) block += '\n' + allItems.join('\n');
      if (notes.length > 0) block += '\nOrientações/observações: ' + notes.join(' | ');
      sections.push(block);
    }
  }

  // ── Óculos (principal + extras) ──
  if (flags.oculos) {
    const lensBlocks: string[] = [];
    if (drafts.oculos) {
      const { od, oe, observacoes } = drafts.oculos;
      lensBlocks.push(
        `OD: ${fmtLens(od)}\nOE: ${fmtLens(oe)}` +
          (observacoes.trim() ? `\nObs: ${observacoes}` : ''),
      );
    }
    for (const inst of extraInstances) {
      if (inst.kind !== 'oculos') continue;
      const { od, oe, observacoes } = inst.rx;
      const label = inst.label ? ` (${inst.label})` : '';
      lensBlocks.push(
        `Receita${label}:\nOD: ${fmtLens(od)}\nOE: ${fmtLens(oe)}` +
          (observacoes.trim() ? `\nObs: ${observacoes}` : ''),
      );
    }
    if (lensBlocks.length > 0) {
      sections.push(
        '### Receita de óculos já prescrita manualmente pelo médico (NÃO altere):\n' +
          lensBlocks.join('\n\n'),
      );
    }
  }

  // ── Atestado (principal + extras) ──
  if (flags.atestado) {
    const blocks: string[] = [];
    const mainText = toPrompt(drafts.atestado);
    if (mainText) blocks.push(mainText);
    for (const inst of extraInstances) {
      if (inst.kind !== 'atestado') continue;
      const t = toPrompt(inst.text);
      if (t) blocks.push(inst.label ? `[${inst.label}]\n${t}` : t);
    }
    if (blocks.length > 0) {
      sections.push(
        '### Atestado(s) já escrito(s) manualmente pelo médico (NÃO altere):\n' + blocks.join('\n---\n'),
      );
    }
  }

  // ── Cirurgias (principal + extras) ──
  // Mesma regra dos exames: legadas ('cirurgias') seguem a flag do tipo;
  // combinadas ('solicitacao') usam a flag da própria instância.
  {
    const blocks: string[] = [];
    if (flags.cirurgias) {
      const mainText = toPrompt(
        serializeSurgeryRequest(drafts.cirurgiasProcs ?? [], drafts.cirurgias),
      );
      if (mainText) blocks.push(mainText);
    }
    for (const inst of extraInstances) {
      if (inst.kind === 'cirurgias' && flags.cirurgias) {
        const t = toPrompt(inst.text);
        if (t) blocks.push(inst.label ? `[${inst.label}]\n${t}` : t);
      } else if (inst.kind === 'solicitacao' && inst.useInAI && inst.includeSurg) {
        const t = toPrompt(serializeSurgeryRequest(inst.surgProcs ?? [], inst.surgText));
        if (t) blocks.push(inst.label ? `[${inst.label}]\n${t}` : t);
      }
    }
    if (blocks.length > 0) {
      sections.push(
        '### Solicitação de cirurgias/procedimentos já escrita pelo médico (NÃO altere):\n' + blocks.join('\n---\n'),
      );
    }
  }

  // ── Declaração / Laudo (principal + extras) ──
  if (flags.declaracao) {
    const blocks: string[] = [];
    const mainText = toPrompt(drafts.declaracao);
    if (mainText) blocks.push(mainText);
    for (const inst of extraInstances) {
      if (inst.kind !== 'declaracao') continue;
      const t = toPrompt(inst.text);
      if (t) blocks.push(inst.label ? `[${inst.label}]\n${t}` : t);
    }
    if (blocks.length > 0) {
      sections.push(
        '### Laudo(s)/Declaração(ões) já escrito(s) pelo médico (NÃO altere):\n' + blocks.join('\n---\n'),
      );
    }
  }

  const encaminhamentoText = toPrompt(drafts.encaminhamento);
  if (flags.encaminhamento && encaminhamentoText) {
    sections.push(
      '### Encaminhamento já escrito pelo médico (NÃO altere):\n' + encaminhamentoText,
    );
  }

  // ── Laudo médico (preenchido manualmente) ──
  // Não é alvo automático da IA (vide structuredResultToDrafts), mas quando o
  // médico escreve um laudo à mão ele é contexto clínico de primeira ordem.
  const laudoText = toPrompt(drafts.laudo);
  if (flags.laudo && laudoText) {
    sections.push(
      '### Laudo médico já escrito pelo médico (NÃO altere):\n' + laudoText,
    );
  }

  // ── Sugestão de conduta (preenchida manualmente) ──
  const condutaText = toPrompt(drafts.conduta);
  if (flags.conduta && condutaText) {
    sections.push(
      '### Conduta/orientações já escritas pelo médico (use como contexto clínico, NÃO contradiga):\n' +
        condutaText,
    );
  }

  return sections.length > 0 ? '\n\n' + sections.join('\n\n') : '';
}

/**
 * Serializa os registros ANTERIORES marcados pelo médico num bloco de
 * contexto pra extração. Deixa explícito que são de OUTRAS datas (histórico),
 * pra a IA usar como antecedente/referência e NÃO copiar como se fossem do
 * atendimento de hoje. Vazio = nenhum registro marcado.
 */
export function serializeSelectedHistoryForAI(
  records: SelectedHistoryRecord[] = [],
): string {
  const items = records.filter((r) => r.text.trim());
  if (items.length === 0) return '';
  const fmtDate = (ms: number): string => formatDateBR(ms, 's/ data');
  const blocks = items.map(
    (r) => `[${fmtDate(r.dateMs)} · ${r.label}]\n${htmlToPlainText(r.text).trim()}`,
  );
  return (
    '### Registros ANTERIORES do prontuário selecionados pelo médico como contexto ' +
    '(histórico de OUTRAS datas — use como antecedente/referência clínica; NÃO copie como se fossem deste ' +
    'atendimento, NÃO altere medidas e NÃO invente além do que está escrito):\n' +
    blocks.join('\n---\n')
  );
}

/**
 * Pré-preenche a Receita de Óculos com a refração DINÂMICA já digitada nos
 * exames do atendimento — evita digitar as mesmas medidas duas vezes.
 * Fontes, em ordem:
 *   1. módulo legado 'refracao-subj-dinamica' (esferico/cilindrico/eixo/adicao);
 *   2. módulo unificado 'refracao' (subj_*), desde que NÃO marcado
 *      "Sob cicloplegia" — a receita sai da refração dinâmica.
 * Só age com o rascunho de óculos VAZIO (não sobrescreve o que o médico ou a
 * IA já puseram) e quando a fonte tem algum valor. Valores copiados VERBATIM
 * (regra dos valores exatos) — o médico revisa no editor antes de imprimir.
 */
export function seedOculosFromEncounterRefraction(s: SessionDraft): SessionDraft {
  // Receita de óculos já emitida neste atendimento → o modal reabre EM
  // BRANCO. Sem isso o seed repõe a mesma refração e o editor parece estar
  // mostrando de volta a receita que o médico acabou de salvar/imprimir.
  if (s.issuedDocs?.oculos) return s;
  const lens = s.drafts.oculos;
  const eyeEmpty = (e?: AILensRx['od']) =>
    !e || (!e.esferica && !e.cilindrica && !e.eixo && !e.adicao && !e.dp && !e.av);
  const lensEmpty = !lens || (eyeEmpty(lens.od) && eyeEmpty(lens.oe) && !lens.observacoes?.trim());
  if (!lensEmpty) return s;

  const entries = s.encounterExams?.entries ?? [];
  type RxKeys = [esf: string, cil: string, eixo: string, adicao: string];
  const pickEye = (
    vals: Record<string, { OD?: string; OE?: string; mono?: string }>,
    [esf, cil, eixo, adicao]: RxKeys,
    eye: 'OD' | 'OE',
  ): AILensRx['od'] => ({
    esferica: vals[esf]?.[eye]?.trim() ?? '',
    cilindrica: vals[cil]?.[eye]?.trim() ?? '',
    eixo: vals[eixo]?.[eye]?.trim() ?? '',
    adicao: vals[adicao]?.[eye]?.trim() ?? '',
    dp: '',
    av: '',
  });
  const build = (vals: Record<string, { OD?: string; OE?: string; mono?: string }>, keys: RxKeys) => {
    const od = pickEye(vals, keys, 'OD');
    const oe = pickEye(vals, keys, 'OE');
    if (eyeEmpty(od) && eyeEmpty(oe)) return null;
    return { od, oe, observacoes: '' };
  };

  let rx: AILensRx | null = null;
  const legacy = entries.find((e) => e.moduleId === 'refracao-subj-dinamica');
  if (legacy) rx = build(legacy.values, ['esferico', 'cilindrico', 'eixo', 'adicao']);
  if (!rx) {
    const uni = entries.find((e) => e.moduleId === 'refracao');
    const ciclo = uni?.values['cicloplegia']?.mono?.trim() ?? '';
    if (uni && ciclo !== 'Sob cicloplegia') {
      rx = build(uni.values, ['subj_esferico', 'subj_cilindrico', 'subj_eixo', 'subj_adicao']);
    }
  }
  if (!rx) return s;
  return { ...s, drafts: { ...s.drafts, oculos: rx } };
}
